import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  TariffEditForm,
  TariffEditInitialValues,
  TariffComponents,
  TariffAdditionalParams,
  TariffFormOptions,
  onTariffFormSaved,
  ITariffFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedTariff, ISharedPatchTariff, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchTariff, patchTariff, getTariffUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useTariffParents, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedTariff> {}

const TariffFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const tariff_parents = useTariffParents();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchTariff(id).then(tariff => setValue(tariff));
  }, [id, setValue, ]);

  const param_props: ITariffFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    tariff_parents: tariff_parents.tariff_parents,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && TariffEditForm(value, param_props, t), [value, tariff_parents, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && TariffFormOptions(value, param_props, t), [value, tariff_parents, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchTariff, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchTariff) => value && save(() => patchTariff(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onTariffFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!tariff_parents.loaded || !organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.Tariff, value)}
    >
      <AtlasForm<ISharedPatchTariff>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={TariffComponents}
        additionalParams={TariffAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={TariffEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('TARIFF')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const TariffFormEditView = React.memo(withEdit(TariffFormEdit, getTariffUpdateSchema));
